/*
  File: {file_name}
  Author: Binish Mookken
  Date: April-June 2024

  Description:

*/

/*
  File: Login.css
  Author: Binish Mookken
  Date: April-June 2024

  Description:

*/

import { Popover, Checkbox, Input, Table } from "antd";
import { useState, useMemo, useCallback } from "react";
import { Agent, AddAgentFormData } from "./types";
import { debounce } from "lodash";

interface ReferralProps {
  existingAgents: Agent[];
  formData: AddAgentFormData;
  onFormDataChange: (data: Partial<AddAgentFormData>) => void;
}

const ReferralComponent: React.FC<ReferralProps> = ({
  existingAgents,
  formData,
  onFormDataChange,
}) => {
  const { referringAgent } = formData;
  const [filteredAgents, setFilteredAgents] = useState<Agent[]>(existingAgents);
  const [sortOrder, setSortOrder] = useState<"ascend" | "descend" | null>(null);
  const [searchText, setSearchText] = useState<string>("");

  const content = (agent: Agent) => {
    return (
      <div className="agent-details">
        <div className="agent-details-column">
          <div className="agent-label">Email:</div>
          <span>{agent?.email}</span>
        </div>
        <div className="agent-details-column">
          <div className="agent-label">Phone Number:</div>
          <span>{agent?.phone_number}</span>
        </div>
        <div className="agent-details-column">
          <div className="agent-label">Referred By:</div>
          <span>
            {agent.referred_by?.first_name + " " + agent.referred_by?.last_name}
          </span>
        </div>
        <div className="agent-details-column">
          <div className="agent-label">Referred Date:</div>
          <span>
            {agent && new Date(agent.referred_date).toLocaleDateString()}
          </span>
        </div>
      </div>
    );
  };

  const [arrow, setArrow] = useState("Show");
  const mergedArrow = useMemo(() => {
    if (arrow === "Hide") {
      return false;
    }

    if (arrow === "Show") {
      return true;
    }

    return {
      pointAtCenter: true,
    };
  }, [arrow]);

  // const handleSearch = (selectedKeys: any, confirm: any, dataIndex: any) => {
  //   if (selectedKeys == "") {
  //     setFilteredAgents(existingAgents);
  //     return;
  //   }
  //   setSearchText(selectedKeys[0]);
  //   const filtered = existingAgents.filter((agent) => {
  //     const searchTerm = selectedKeys[0].toLowerCase();
  //     return (
  //       agent.first_name.toLowerCase().startsWith(searchTerm) ||
  //       agent.last_name.toLowerCase().startsWith(searchTerm)
  //     );
  //   });
  //   setFilteredAgents(filtered);
  // };

  const handleReset = (clearFilters: any) => {
    clearFilters();
    setSearchText("");
    setFilteredAgents(existingAgents);
  };

  const handleSortChange = (pagination: any, filters: any, sorter: any) => {
    setSortOrder(sorter.order);
    const sortedAgents = existingAgents
      .slice()
      .sort((a, b) =>
        sorter.order === "descend"
          ? b.first_name.localeCompare(a.first_name)
          : a.first_name.localeCompare(b.first_name)
      );
    setFilteredAgents(sortedAgents);
  };

  const getFullName = (agent: Agent) =>
    `${agent.first_name} ${agent.last_name}`;

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      sorter: true,
      sortOrder,
      render: (text: string, record: Agent) => (
        <Popover
          style={{ background: "white" }}
          content={() => content(record)}
          title={getFullName(record)}
          placement="left"
          arrow={true}
        >
          <span>{getFullName(record)}</span>
        </Popover>
      ),
      width: "auto",
    },
    {
      title: "Referred By",
      dataIndex: "referred_by",
      render: (text: string, record: Agent) =>
        record.referred_by
          ? `${record.referred_by.first_name} ${record.referred_by.last_name}`
          : "House Account",
    },
    {
      title: "My Passive Income Network",
      dataIndex: "referrals",
      render: (text: string, record: Agent) =>
        record.referrals ? record.referrals.length : 0,
    },
    {
      title: "Select",
      key: "select",
      render: (agent: Agent) => (
        <Checkbox
          checked={referringAgent && referringAgent.id === agent.id}
          onChange={(e) =>
            onFormDataChange({
              referringAgent: e.target.checked ? agent : null,
            })
          }
        />
      ),
    },
  ];

  const handleRowClick = (record: Agent) => {
    onFormDataChange({
      referringAgent:
        referringAgent && referringAgent.id === record.id ? null : record,
    });
  };

  const handleSearch = (searchTerm: string) => {
    if (searchTerm === "") {
      setFilteredAgents(existingAgents);
      return;
    }

    const filtered = existingAgents.filter((agent) => {
      const term = searchTerm.toLowerCase();
      return (
        agent.first_name.toLowerCase().startsWith(term) ||
        agent.last_name.toLowerCase().startsWith(term)
      );
    });
    setFilteredAgents(filtered);
  };

  const debouncedSearch = useCallback(
    debounce((value) => handleSearch(value), 300),
    [existingAgents]
  );

  const onChange = (e: { target: { value: any } }) => {
    const value = e.target.value;
    setSearchText(value);
    debouncedSearch(value); // Trigger the search function on input change with debounce
  };

  return (
    <div style={{ marginTop: "30px" }}>
      <div className="step-header">Select referring agent</div>
      <div className="step-header-subtext">
        Select the agent who referred the new agent from the list below (use the
        search box to refine the list as needed). Select "House Account" if no
        referring agent exists.
      </div>
      <Input.Search
        placeholder="Search by name"
        allowClear
        enterButton
        onChange={onChange}
        onReset={handleReset}
        style={{ marginBottom: 16 }}
      />
      <Table
        dataSource={filteredAgents}
        columns={columns}
        rowKey="id"
        pagination={false}
        onChange={handleSortChange}
        rowClassName={(record) =>
          referringAgent && referringAgent.id === record.id
            ? "selected-row"
            : ""
        }
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
        })}
        style={{ height: "400px", overflow: "auto" }}
      />
    </div>
  );
};

export default ReferralComponent;
